import React from "react";
import { Box } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { toast } from "react-toastify";
import { COLORS } from "../../assets/theme";
import CustomTable from "../../components/table/CustomTable";
import SearchHeaderContainer from "../../components/table/SearchHeaderContainer";
import Actions from "../../components/Actions";
import GenericFormDialog, { Field } from "../../components/GenericFormDialog";
import ConfirmDialog from "../../components/ConfirmDialog";
import PageHeader from "../../components/PageHeader";
import { useLocation, useParams } from "react-router-dom";

import {
  applyServicesToUnits,
  createService,
  deleteService,
  getAllServices,
  updateService,
} from "../../services/ServicesService";
import { getAllBranches } from "../../services/BranchesService";

const Services: React.FC = () => {
  const location = useLocation();
const params = useParams<{ businessId?: string; id?: string }>();
const venueId = params.businessId || params.id;

  const [units, setUnits] = React.useState<any[]>([]);
  const [selectValue, setSelectValue] = React.useState<
    string | number | (string | number)[]
  >("");
  const [selectCleared, setSelectCleared] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [editingRow, setEditingRow] = React.useState<any | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [toDeleteId, setToDeleteId] = React.useState<string | number | null>(
    null
  );
  const [applyConfirmOpen, setApplyConfirmOpen] = React.useState(false);
  const [applying, setApplying] = React.useState(false);

  const [search, setSearch] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [refetchTrigger, setRefetchTrigger] = React.useState(0);
  const [addInitialValues, setAddInitialValues] = React.useState<
    Record<string, any>
  >({
    price_discount: "0.00",
    order: "",
    active: true,
  });

  const formFields: Field[] = [
    {
      name: "unit_id",
      label: "Unit",
      type: "dropdown",
      options: units.map((b: any) => ({
        label: b.name || b.unit_name || "Unknown Unit",
        value: String(b.id),
      })),
      searchable: true,
      required: true,
    },
    { name: "name", label: "Service Name" , required: true},
    { name: "price", label: "Price" , required: true},
    { name: "duration", label: "Duration (minutes)" , required: true},
    { name: "description", label: "Description" },
    {name:'order', label:'Order'},
    { name: "active", label: "Active", type: "dropdown", options: [
      { label: "Active", value: "Active" },
      { label: "Inactive", value: "Inactive" },
    ] , required: true},
    { name: "max_clients", label: "Max Clients" },
  ];

  const openEditForId = (id: string | number) => {
    const found = rows.find((r: any) => String(r.id) === String(id));
    if (found) {
      setEditingRow(found);
      setEditOpen(true);
    }
  };
  const wiredActions = Actions({
    handleOpenEditModal: { handler: openEditForId },
    onDelete: {
      handler: async (id: number | string) => {
        setToDeleteId(id);
        setConfirmOpen(true);
      },
    },
  });

  const [rows, setRows] = React.useState<any[]>([]);

  const selectedUnit = units.find((u) => String(u.id) === String(selectValue));
  const otherUnitsCount = Math.max(units.length - 1, 0);

  const handleApplyToUnits = async () => {
    if (applying || !selectValue || !venueId) return;
    setApplying(true);
    try {
      const response: any = await applyServicesToUnits({
        sourceUnitId: String(selectValue),
        businessId: String(venueId),
        skipIfNameExists: true,
      });

      const results = response?.results || [];
      const totalCreated = results.reduce(
        (sum: number, r: any) => sum + (r.created?.length || 0),
        0
      );
      const totalSkipped = results.reduce(
        (sum: number, r: any) => sum + (r.skipped?.length || 0),
        0
      );

      toast.success(
        `Applied to ${results.length} court${results.length === 1 ? "" : "s"}: ${totalCreated} service${totalCreated === 1 ? "" : "s"} created, ${totalSkipped} skipped.`,
        { position: "top-right" }
      );
      setApplyConfirmOpen(false);
    } catch (error: any) {
      console.error("Error applying services to units:", error);
      toast.error(
        getErrorMessage(error, "Failed to apply services to the other courts."),
        { position: "top-right" }
      );
    } finally {
      setApplying(false);
    }
  };

  const getErrorMessage = (error: any, fallback: string) => {
    return (
      error?.response?.data?.message ||
      error?.response?.data?.msg ||
      error?.response?.data?.error ||
      error?.message ||
      fallback
    );
  };React.useEffect(() => {
  const fetchUnits = async () => {
    try {
      if (!venueId) {
        setUnits([]);
        setSelectValue("");
        return;
      }

      const response = await getAllBranches(venueId);

      let unitsData: any[] = [];
      if (Array.isArray(response)) {
        unitsData = response;
      } else if (response?.data) {
        unitsData = response.data;
      } else if (response?.units) {
        unitsData = response.units;
      }

      const processedUnits = unitsData.map((u: any) => ({
        id: String(u.id ?? u.unit_id ?? u._id),
        name: u.name || u.unit_name || "Unknown Unit",
      }));

      setUnits(processedUnits);

      if (processedUnits.length > 0 && !selectValue) {
        setSelectValue(processedUnits[0].id);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to fetch units."));
      setUnits([]);
      setSelectValue("");
    }
  };

  fetchUnits();
}, [venueId]);

  // Only fetch services when a unit is selected
  React.useEffect(() => {
    if (!selectValue) {
      setRows([]);
      return;
    }
    const fetchSpecialities = async () => {
      setLoading(true);
      try {
        const unitId = selectValue;
        const response = await getAllServices(String(unitId)) as ServiceResponse;
        console.log("Services fetched:", response);
        type ServiceResponse = {
          data?: {
            services?: any[];
          } | any[];
          services?: any[];
        };

        let specialitiesData: any[] = [];
        if (response && Array.isArray(response)) {
          specialitiesData = response;
        } else if (response && response.data) {
          specialitiesData = Array.isArray(response.data)
            ? response.data
            : response.data.services || [];
        } else if (response && response.services) {
          specialitiesData = response.services;
        }

        const mapped = (specialitiesData || []).map((s: any) => {
          return {
            id: s.id ?? s._id,
            name: s.name ?? s.title ?? "",
            price: s.price ?? "",
            duration: s.duration ?? "",
            description: s.description ?? "",
            order: s.order ?? "",
            active: s.active ?? true,
            max_clients: s.max_clients ?? "",
            unit_id: s.unit_id ?? s.unitId ?? "",
          };
        });

        setRows(mapped);
      } catch (error: any) {
        console.error("Error fetching services:", error);
        toast.error(getErrorMessage(error, "Failed to fetch services."), {
          position: "top-right",
        });
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSpecialities();
  }, [refetchTrigger, selectValue, venueId]);

  return (
    <Box sx={{ width: "100%", maxWidth: "90vw", overflowX: "hidden" }}>
      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
        <PageHeader
          title="Services"
          subtitle="The services clients can book, per branch."
        />
        <SearchHeaderContainer
          search={search}
          setSearch={setSearch}
          selectConfig={{
            options: units.map((unit) => ({
              label: unit.name || "Unknown Unit",
              value: unit.id || "",
            })),
            value: selectValue,
            onChange: (v: any) => {
              const isEmpty =
                v === "" || v === null || (Array.isArray(v) && v.length === 0);
              if (isEmpty) {
                setSelectValue("");
                setSelectCleared(true);
                return;
              }

              setSelectCleared(false);
              setSelectValue(v);
            },
            label: "Select Unit",

            placeholder: "Select unit",
            size: "medium",
            searchable: false,
            clearable: true,
            multiSelect: false,
            sx: {
              backgroundColor: COLORS.WhiteBackground,
            },
          }}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.MainColor,
              startIcon: <AddIcon />,
              onClick: () => {
                setEditingRow(null);
                setEditOpen(true);
              },
              textColor: COLORS.WhiteText,
              minHeight: 36,
              maxHeight: 36,
              sx: {
                width: { xs: "96%", sm: 36 },
                minWidth: { sm: 36 },
                marginRight: { xs: 1.5, sm: 0 },
              },
            },
            {
              size: "small",
              variant: "outlined",
              label: "Apply to All Courts",
              startIcon: <ContentCopyIcon />,
              disabled: !selectValue || otherUnitsCount === 0 || rows.length === 0,
              onClick: () => setApplyConfirmOpen(true),
              minHeight: 36,
              maxHeight: 36,
              sx: {
                width: { xs: "96%", sm: "auto" },
                marginRight: { xs: 1.5, sm: 0 },
              },
            },
          ]}
        />
        <CustomTable<any>
          filters={{ page: 0, size: 10, search }}
          clientSidePagination={true}
          loading={loading}
          fields={[
            { key: "name", header: "Service Name" },
            {
              key: "price",
              header: "Price",
              render: (row: any) => {
                const raw = row?.price;
                if (raw === undefined || raw === null || raw === "") return "-";
                const text = String(raw);
                return text.trim().endsWith("$") ? text : `${text} $`;
              },
            },
            { key: "duration", header: "Duration" },
            { key: "order", header: "Order" },
            { key: "description", header: "Description" },
            { key: "active", header: "Active" },
          ]}
          actions={wiredActions}
          fetchFunc={async (params?: any) => {
            const q = (params && params.search) || search || "";

            const normalizedQuery = String(q || "")
              .toLowerCase()
              .trim();

            const filtered = rows.filter((r: any) => {
              if (!normalizedQuery) return true;

              const haystack = [
                r.name,
                r.price,
                r.duration,
                r.description,
                r.active,
              ]
                .filter(Boolean)
                .map((v: any) => String(v).toLowerCase())
                .join(" ");

              return haystack.includes(normalizedQuery);
            });

            const content = filtered;
            const totalElements = content.length;
            const pageSize = (params && params.size) || 10;
            const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));

            return {
              content,
              number: 0,
              size: pageSize,
              totalElements,
              totalPages,
            };
          }}
        />
        <GenericFormDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          title={editingRow ? `Edit Service` : "Add Service"}
          fields={editingRow ? formFields : [...formFields]}
          initialValues={
            editingRow
              ? {
                  id: String(editingRow.id ?? ""),
                  name: String(editingRow.name || ""),
                  price: String(editingRow.price || ""),
                  duration: String(editingRow.duration || ""),
                  description: String(editingRow.description || ""),
                  order: String(editingRow.order || ""),
                  active: editingRow.active === false || editingRow.active === "Inactive" ? "Inactive" : "Active",
                  max_clients: String(editingRow.max_clients || ""),
                  unit_id:
                    String(editingRow.unit_id ?? selectValue ?? venueId ?? ""),
                }
              : {
                  name: "",
                  price: "",
                  duration: "",
                  description: "",
                  order: "",
                  active: "Active",
                  max_clients: "",
                  ...addInitialValues,
                  unit_id:
                    selectValue !== "" &&
                    selectValue !== null &&
                    selectValue !== undefined
                      ? String(selectValue)
                      : addInitialValues.unit_id
                      ? String(addInitialValues.unit_id)
                      : venueId
                      ? String(venueId)
                      : "",
                }
          }
          onSubmit={async (values) => {
            try {
                  // Prefer branch selection from the form (values.branch_id) then fallback to dropdown searchValue or venueId
                  const unitId = values.unit_id
                    ? String(values.unit_id)
                    : selectValue
                    ? String(selectValue)
                    : venueId;

              if (editingRow) {
                await updateService(String(editingRow.id), {
                  id: editingRow.id,
                  name: String(values.name),
                  description: values.description,
                  duration: Number(values.duration), // always store as number (minutes)
                  order: values.order ? Number(values.order) : null,
                  max_clients: Number(values.max_clients),
                  active: values.active === "Active" ? true : false,
                  price: Number(values.price),
                  price_discount: values.price_discount ?? "0.00",
                  currency_id: values.currency_id,
                  unitId,
                });
                toast.success("Service updated successfully!", {
                  position: "top-right",
                });
                setRefetchTrigger((prev) => prev + 1);
              } else {
                await createService({
                  name: String(values.name),
                  description: values.description,
                  duration: Number(values.duration), // always store as number (minutes)
                  order: values.order ? Number(values.order) : null,
                  max_clients: Number(values.max_clients),
                  active: values.active === "Active" ? true : false,
                  price: Number(values.price),
                  price_discount: values.price_discount ?? "0.00",
                  currency_id: values.currency_id,
                  unitId,
                });
                toast.success("Service created successfully!", {
                  position: "top-right",
                });
                setRefetchTrigger((prev) => prev + 1);
              }
              setEditOpen(false);
              setEditingRow(null);
            } catch (error: any) {
              console.error("Error saving service:", error);
              const errorMessage = getErrorMessage(
                error,
                "Failed to save service. Please try again."
              );
              toast.error(errorMessage, { position: "top-right" });
            }
          }}
        />
        <ConfirmDialog
          open={confirmOpen}
          onClose={() => {
            setConfirmOpen(false);
            setToDeleteId(null);
          }}
          title="Delete entry"
          message="Are you sure you want to delete this entry? This action cannot be undone."
          onConfirm={async () => {
            if (toDeleteId === null) return;
            try {
              await deleteService(String(toDeleteId));
              toast.success("Service deleted successfully!", {
                position: "top-right",
              });
              setRefetchTrigger((prev) => prev + 1);
            } catch (error: any) {
              console.error("Error deleting service:", error);
              const errorMessage = getErrorMessage(
                error,
                "Failed to delete service. Please try again."
              );
              toast.error(errorMessage, { position: "top-right" });
            } finally {
              setConfirmOpen(false);
              setToDeleteId(null);
            }
          }}
        />
        <ConfirmDialog
          open={applyConfirmOpen}
          onClose={() => {
            if (!applying) setApplyConfirmOpen(false);
          }}
          title="Apply services to all courts"
          message={`This will copy the active services from "${
            selectedUnit?.name || "the selected court"
          }" to the other ${otherUnitsCount} court${
            otherUnitsCount === 1 ? "" : "s"
          } in this business. Services with a name that already exists on a court will be skipped.`}
          confirmButtonText={applying ? "Applying..." : "Apply"}
          confirmButtonColor={COLORS.MainColor}
          onConfirm={handleApplyToUnits}
        />
      </Box>
    </Box>
  );
};

export default Services;
